(() => {
  if (customElements.get('am2-visual-menu')) return;

  class AM2VisualMenu extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected || this.controller) return;
        this.controller = new AbortController();
        this.tabs = Array.from(this.querySelectorAll('[data-am2-tab]'));
        this.panels = Array.from(this.querySelectorAll('[data-am2-panel]'));
        this.header = this.closest('[data-section-type="header"]');
        this.trigger = Array.from(this.header.querySelectorAll('[data-meganav-trigger]'))
          .find(trigger => trigger.getAttribute('aria-controls') === this.id);
        if (!this.trigger || !this.tabs.length) return;
        const listen = (target, type, handler, options = {}) => {
          target.addEventListener(type, handler, { ...options, signal: this.controller.signal });
        };

        this.tabs.forEach((tab, index) => {
          listen(tab, 'click', () => this.selectTab(tab));
          listen(tab, 'pointerenter', event => {
            if (event.pointerType !== 'mouse') return;
            if (this.panels.some(panel => panel.contains(document.activeElement))) return;
            clearTimeout(this.hoverTimer);
            this.hoverTimer = setTimeout(() => this.selectTab(tab), 160);
          });
          listen(tab, 'pointerleave', () => clearTimeout(this.hoverTimer));
          listen(tab, 'keydown', event => {
            let next;
            if (event.key === 'ArrowDown') next = (index + 1) % this.tabs.length;
            if (event.key === 'ArrowUp') next = (index - 1 + this.tabs.length) % this.tabs.length;
            if (event.key === 'Home') next = 0;
            if (event.key === 'End') next = this.tabs.length - 1;
            if (next !== undefined) {
              event.preventDefault();
              this.selectTab(this.tabs[next]);
              this.tabs[next].focus();
            }
            if (event.key === 'ArrowRight') {
              event.preventDefault();
              this.panels.find(panel => !panel.hidden)?.querySelector('a[href]')?.focus();
            }
          });
        });

        listen(this.trigger, 'keydown', event => {
          if (!['Enter', ' ', 'ArrowDown'].includes(event.key)) return;
          event.preventDefault();
          this.open();
          this.tabs.find(tab => tab.getAttribute('aria-selected') === 'true')?.focus();
        });
        listen(this.trigger, 'pointerdown', event => {
          this.touchOpen = event.pointerType !== 'mouse' && !this.classList.contains('active');
        }, { capture: true });
        listen(this.trigger, 'click', event => {
          if (this.touchOpen) {
            event.preventDefault();
            this.open();
          }
          this.touchOpen = false;
        });
        listen(this, 'keydown', event => {
          if (event.key !== 'Escape') return;
          event.preventDefault();
          event.stopPropagation();
          this.close(true);
        });
        listen(this, 'focusout', event => {
          if (event.relatedTarget && !this.contains(event.relatedTarget) && event.relatedTarget !== this.trigger) this.close();
        });
        listen(window, 'resize', () => {
          if (window.innerWidth < 960 && this.classList.contains('active')) this.close();
          this.updateHeight();
        });
        this.menuObserver = new MutationObserver(() => this.updateHeight());
        this.menuObserver.observe(this, { attributes: true, attributeFilter: ['class'] });

        this.mobileList = Array.from(this.header.querySelectorAll('[data-am2-visual-mobile]'))
          .find(list => list.dataset.am2VisualMobile === this.dataset.menuHandle);
        if (this.mobileList) {
          this.mobileDetails = Array.from(this.mobileList.querySelectorAll('details'));
          this.mobileDetails.forEach(details => {
            listen(details, 'toggle', () => {
              if (details.open) this.mobileDetails.forEach(other => { if (other !== details) other.open = false; });
              this.updateMobileHeight();
            });
          });
          this.mobileObserver = new ResizeObserver(() => this.updateMobileHeight());
          this.mobileObserver.observe(this.mobileList);
        }

        listen(document, 'shopify:block:select', event => {
          const tab = this.tabs.find(candidate => candidate.dataset.am2Tab === event.detail?.blockId);
          if (!tab) return;
          this.editorBlock = event.detail.blockId;
          this.selectTab(tab);
          requestAnimationFrame(() => {
            if (window.innerWidth >= 960) {
              this.open();
            } else if (this.mobileList) {
              const menuButton = this.header.querySelector('[data-js-menu-button]');
              if (menuButton?.getAttribute('aria-expanded') !== 'true') menuButton?.click();
              if (!this.mobileList.classList.contains('visible')) {
                const primary = this.mobileList.closest('[data-primary-container]');
                // Return through any open drawer levels before entering this parent.
                Array.from(primary?.querySelectorAll('.drawer-menu__list--sub.visible') || []).reverse()
                  .forEach(list => list.querySelector('[data-item="back"]')?.click());
                this.mobileList.previousElementSibling?.click();
              }
              const details = this.mobileDetails.find(item => item.dataset.am2MobileBlock === this.editorBlock);
              if (details) details.open = true;
            }
          });
        });
        listen(document, 'shopify:block:deselect', event => {
          if (event.detail?.blockId === this.editorBlock) {
            this.editorBlock = null;
            this.close();
          }
        });
      });
    }

    selectTab(tab) {
      clearTimeout(this.hoverTimer);
      this.tabs.forEach(candidate => {
        const selected = candidate === tab;
        candidate.setAttribute('aria-selected', String(selected));
        candidate.tabIndex = selected ? 0 : -1;
      });
      this.panels.forEach(panel => { panel.hidden = panel.dataset.am2Panel !== tab.dataset.am2Tab; });
      this.updateHeight();
    }

    open() {
      // Let Stiletto own the outer dropdown, sticky header and shade overlay.
      this.trigger.dispatchEvent(new MouseEvent('mouseover', { bubbles: true }));
      this.updateHeight();
    }

    close(restoreFocus = false) {
      clearTimeout(this.hoverTimer);
      if (restoreFocus) this.trigger.focus({ preventScroll: true });
      this.trigger.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', keyCode: 27, which: 27, bubbles: true }));
    }

    updateHeight() {
      if (!this.classList.contains('active')) return;
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const available = Math.max(120, viewportHeight - Math.max(0, this.getBoundingClientRect().top) - 16);
      this.style.setProperty('--am2-vm-available', `${Math.floor(available)}px`);
    }

    updateMobileHeight() {
      if (!this.mobileList?.classList.contains('visible')) return;
      const main = this.mobileList.closest('[data-main]');
      if (main) main.style.height = `${this.mobileList.offsetHeight + 8}px`;
    }

    disconnectedCallback() {
      clearTimeout(this.hoverTimer);
      this.controller?.abort();
      this.controller = null;
      this.menuObserver?.disconnect();
      this.mobileObserver?.disconnect();
    }
  }

  customElements.define('am2-visual-menu', AM2VisualMenu);
})();
